import * as React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  Flag,
  Clock,
  Eye,
  MessageCircle,
  Building2,
  Star,
  ImageOff,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { formatClp } from '@/lib/format/currency';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Condition = 'nuevo' | 'como_nuevo' | 'buen_estado' | 'usado';

interface SellerInfo {
  name: string;
  avatarUrl?: string;
  rating: number;
  reviewCount: number;
  isSameBuilding?: boolean;
  condominiumName: string;
}

interface ProductDetailHeroProps {
  /** Array of image URLs; first is the hero */
  images: string[];
  /** Listing title */
  title: string;
  /** Price in CLP integer */
  priceCLP: number;
  /** Item condition */
  condition: Condition;
  /** Listing description */
  description: string;
  /** Seller information */
  seller: SellerInfo;
  /** ISO timestamp */
  createdAt: string;
  /** View count */
  viewCount?: number;
  /** Is this favorited by the current user */
  isFavorited?: boolean;
  /** Callbacks */
  onToggleFavorite?: () => void;
  onContact?: () => void;
  onShare?: () => void;
  onReport?: () => void;
  onViewSellerProfile?: () => void;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CONDITION_LABEL: Record<Condition, string> = {
  nuevo: 'Nuevo',
  como_nuevo: 'Como nuevo',
  buen_estado: 'Buen estado',
  usado: 'Usado',
};

const CONDITION_VARIANT: Record<
  Condition,
  'success' | 'primary' | 'neutral' | 'warning'
> = {
  nuevo: 'success',
  como_nuevo: 'primary',
  buen_estado: 'neutral',
  usado: 'warning',
};

/* ------------------------------------------------------------------ */
/*  Image Gallery                                                      */
/* ------------------------------------------------------------------ */

/**
 * ImageGallery
 * ------------
 * Full image viewer with thumbnail strip. On mobile, supports
 * swipe navigation via touch events.
 *
 * Layout:
 * ┌───────────────────────────────┐
 * │                               │
 * │    ◄    MAIN IMAGE     ►     │  <- 4:3 aspect, object-contain
 * │                               │
 * ├───┬───┬───┬───┬───┬──────────┤
 * │ t │ t │ t │ t │ t │          │  <- thumbnail strip, scrollable
 * └───┴───┴───┴───┴───┴──────────┘
 */
function ImageGallery({
  images,
  title,
}: {
  images: string[];
  title: string;
}) {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const touchStartX = React.useRef(0);

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < images.length - 1;

  function goTo(index: number) {
    setActiveIndex(Math.max(0, Math.min(images.length - 1, index)));
  }

  // Touch swipe handling for mobile
  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? 0;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    const touchEndX = e.changedTouches[0]?.clientX ?? 0;
    const diff = touchStartX.current - touchEndX;
    const threshold = 50;
    if (diff > threshold && canNext) goTo(activeIndex + 1);
    if (diff < -threshold && canPrev) goTo(activeIndex - 1);
  }

  if (images.length === 0) {
    return (
      <div className="flex aspect-[4/3] w-full items-center justify-center rounded-md bg-[var(--color-paper-dim)] text-[var(--color-ink-faint)]">
        <ImageOff className="h-12 w-12" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main image */}
      <div
        className="relative aspect-[4/3] overflow-hidden rounded-md bg-[var(--color-paper-dim)]"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={images[activeIndex]}
          alt={`${title}, imagen ${activeIndex + 1} de ${images.length}`}
          className="h-full w-full object-contain"
        />

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Imagen anterior"
              disabled={!canPrev}
              onClick={() => goTo(activeIndex - 1)}
              className={cn(
                'absolute left-2 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full',
                'bg-[color-mix(in_oklch,var(--color-paper)_85%,transparent)] backdrop-blur-sm',
                'border border-[var(--color-hairline)] shadow-sm',
                'transition-all duration-150',
                canPrev
                  ? 'text-[var(--color-ink)] hover:bg-[var(--color-paper)] hover:scale-105'
                  : 'opacity-30 cursor-not-allowed',
              )}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Imagen siguiente"
              disabled={!canNext}
              onClick={() => goTo(activeIndex + 1)}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full',
                'bg-[color-mix(in_oklch,var(--color-paper)_85%,transparent)] backdrop-blur-sm',
                'border border-[var(--color-hairline)] shadow-sm',
                'transition-all duration-150',
                canNext
                  ? 'text-[var(--color-ink)] hover:bg-[var(--color-paper)] hover:scale-105'
                  : 'opacity-30 cursor-not-allowed',
              )}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Image counter pill */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-[var(--color-ink)]/70 px-2.5 py-1 text-[0.625rem] font-semibold text-[var(--color-paper)] backdrop-blur-sm tabular">
            {activeIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {images.map((url, i) => (
            <button
              key={url}
              type="button"
              aria-label={`Ver imagen ${i + 1}`}
              aria-current={i === activeIndex ? 'true' : undefined}
              onClick={() => goTo(i)}
              className={cn(
                'h-16 w-16 shrink-0 overflow-hidden rounded border-2 transition-all duration-150',
                i === activeIndex
                  ? 'border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]/20'
                  : 'border-[var(--color-hairline)] opacity-60 hover:opacity-100 hover:border-[var(--color-hairline-strong)]',
              )}
            >
              <img
                src={url}
                alt=""
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Time-ago helper                                                    */
/* ------------------------------------------------------------------ */

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/**
 * ProductDetailHero
 * -----------------
 * Full product detail view with image gallery, price, description,
 * seller card, and action buttons.
 *
 * Layout (desktop — 2 columns):
 * ┌──────────────────────┬─────────────────────┐
 * │                      │  Title              │
 * │   IMAGE GALLERY      │  $XX.XXX            │
 * │   + thumbnails       │  Condition badge    │
 * │                      │                     │
 * │                      │  [Contactar] [♡]    │
 * │                      │                     │
 * │                      │  ┌─Seller Card────┐ │
 * │                      │  └────────────────┘ │
 * │                      │                     │
 * │                      │  Posted · Views     │
 * ├──────────────────────┴─────────────────────┤
 * │  Description (full text)                    │
 * └─────────────────────────────────────────────┘
 *
 * Mobile: single column, gallery on top.
 */
export function ProductDetailHero({
  images,
  title,
  priceCLP,
  condition,
  description,
  seller,
  createdAt,
  viewCount,
  isFavorited = false,
  onToggleFavorite,
  onContact,
  onShare,
  onReport,
  onViewSellerProfile,
  className,
}: ProductDetailHeroProps) {
  const sellerInitials = seller.name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <div className={cn('space-y-6', className)}>
      {/* ---- Two-column layout ---- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px]">
        {/* Left column: gallery */}
        <ImageGallery images={images} title={title} />

        {/* Right column: details */}
        <div className="flex flex-col gap-5">
          {/* Title */}
          <h1 className="font-display text-2xl font-semibold leading-tight tracking-tight text-[var(--color-ink)] lg:text-3xl">
            {title}
          </h1>

          {/* Price + condition */}
          <div className="flex items-center gap-3">
            <p className="font-display-tight text-4xl font-semibold tracking-tight text-[var(--color-ink)] tabular">
              {formatClp(priceCLP)}
            </p>
            <Badge variant={CONDITION_VARIANT[condition]}>
              {CONDITION_LABEL[condition]}
            </Badge>
          </div>

          {/* Primary CTA row */}
          <div className="flex items-center gap-2">
            {onContact && (
              <Button
                variant="accent"
                size="lg"
                className="flex-1"
                onClick={onContact}
              >
                <MessageCircle className="h-5 w-5" />
                Contactar vendedor
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0"
              aria-label={isFavorited ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              aria-pressed={isFavorited}
              onClick={onToggleFavorite}
            >
              <Heart
                className={cn(
                  'h-5 w-5',
                  isFavorited
                    ? 'fill-[var(--color-accent)] text-[var(--color-accent)]'
                    : 'text-[var(--color-ink-muted)]',
                )}
              />
            </Button>
          </div>

          {/* Seller card (compact) */}
          <button
            type="button"
            onClick={onViewSellerProfile}
            className={cn(
              'flex w-full items-center gap-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4',
              'transition-all duration-150 hover:border-[var(--color-hairline-strong)] hover:bg-[var(--color-paper-dim)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
            )}
          >
            <Avatar className="h-11 w-11">
              <AvatarFallback>{sellerInitials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-[var(--color-ink)]">
                  {seller.name}
                </p>
                {seller.isSameBuilding && (
                  <span className="inline-flex items-center gap-0.5 rounded-sm bg-[var(--color-primary-50)] px-1.5 py-0.5 text-[0.5625rem] font-semibold text-[var(--color-primary)] ring-1 ring-[var(--color-primary)]/20">
                    <Building2 className="h-2.5 w-2.5" />
                    Tu edificio
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star
                      key={i}
                      className={cn(
                        'h-3 w-3',
                        i < Math.round(seller.rating)
                          ? 'fill-[var(--color-ochre)] text-[var(--color-ochre)]'
                          : 'fill-none text-[var(--color-hairline-strong)]',
                      )}
                    />
                  ))}
                </div>
                <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
                  ({seller.reviewCount})
                </span>
                <span className="text-[0.6875rem] text-[var(--color-ink-faint)]">
                  ·
                </span>
                <span className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                  {seller.condominiumName}
                </span>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-ink-faint)]" />
          </button>

          {/* Meta row */}
          <div className="flex items-center gap-4 text-[0.8125rem] text-[var(--color-ink-muted)]">
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-[var(--color-ink-faint)]" />
              {formatDate(createdAt)}
            </span>
            {viewCount !== undefined && (
              <span className="flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5 text-[var(--color-ink-faint)]" />
                {viewCount} vista{viewCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Secondary actions */}
          <div className="flex items-center gap-3 border-t border-[var(--color-hairline)] pt-4">
            {onShare && (
              <button
                type="button"
                onClick={onShare}
                className="flex items-center gap-1.5 text-[0.8125rem] text-[var(--color-ink-muted)] transition-colors hover:text-[var(--color-ink)]"
              >
                <Share2 className="h-3.5 w-3.5" />
                Compartir
              </button>
            )}
            {onReport && (
              <button
                type="button"
                onClick={onReport}
                className="ml-auto flex items-center gap-1.5 text-[0.8125rem] text-[var(--color-ink-faint)] transition-colors hover:text-[var(--color-danger)]"
              >
                <Flag className="h-3.5 w-3.5" />
                Reportar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---- Description ---- */}
      <div className="border-t border-[var(--color-hairline)] pt-6">
        <h2 className="eyebrow mb-3">Descripcion</h2>
        <div className="prose-sm max-w-none text-[0.9375rem] leading-relaxed text-[var(--color-ink-soft)]">
          {description.split('\n').map((paragraph, i) => (
            <p key={i} className={i > 0 ? 'mt-3' : ''}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                           */
/* ------------------------------------------------------------------ */

export function ProductDetailHeroSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn('space-y-6', className)}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
        <Skeleton className="aspect-[4/3] w-full rounded-md" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-20 w-full rounded-md" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
      <div className="border-t border-[var(--color-hairline)] pt-6">
        <Skeleton className="mb-3 h-3 w-24" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-4/5" />
        <Skeleton className="mt-2 h-4 w-3/5" />
      </div>
    </div>
  );
}
